<script lang="ts" setup>
import { XButton, XIcon } from '@clicksign/excalibur';

import { BrandButton } from '~~base-ui';
import { useModals, useCurrentSignatureRequestKey, useEnvironmentInfo, useFlux } from '~~core';
import { useFetchDocumentsList } from '~~document';

import RequirementOptionsModal from '../RequirementOptionsModal/RequirementOptionsModal.vue';
import RequirementRefusalModal from '../RequirementRefusalModal/RequirementRefusalModal.vue';

export type RequirementToolbarEmits = {
  (e: 'go-back'): void
};

export type RequirementToolbarProps = {
  length: number;
  current: number;
  hideProgress?: boolean,
  hideBackButton?: boolean,
};

defineEmits<RequirementToolbarEmits>();
const props = withDefaults(defineProps<RequirementToolbarProps>(), {
  length: 1,
  current: 1,
  hideProgress: false,
  hideBackButton: false,
});

const modalName = {
  options: 'options',
  refusal: 'refusal',
};

const { openModal, closeModal, isModalOpen } = useModals();
const { signatureRequestKey } = useCurrentSignatureRequestKey();
const { isMassiveSignature } = useEnvironmentInfo();
const { isFlux } = useFlux();

const emptyDocuments = { documents: ref([]) };
const { documents } = isFlux || isMassiveSignature.value
  ? emptyDocuments
  : useFetchDocumentsList(signatureRequestKey);

const refusableDocuments = computed(() => {
  if (!documents.value) return [];

  return documents.value
    .filter((document) => document.refusable)
    .map((document) => document.key);
});

const progressPercent = computed(() => (props.current / props.length) * 100);
</script>

<template>
  <div
    :class="[$style.toolbar, hideProgress && $style.hideProgress]"
    :style="{ '--progress': `${progressPercent}%` }"
    data-testid="requirementToolbar"
  >
    <div :class="$style.back">
      <XButton
        v-if="!hideBackButton"
        :class="$style.button"
        design="link"
        size="sm"
        data-testid="toolbarGoBackButton"
        @click="$emit('go-back')"
      >
        <XIcon
          icon="arrow-left"
          left
        />

        <span :class="$style.backText">
          {{ $t('common.back') }}
        </span>
      </XButton>
    </div>

    <span
      v-if="!hideProgress"
      :class="$style.resume"
      data-testid="requirementToolbarResume"
    >
      {{ $t('requirement.toolbar.resume', { active: current, total: length }) }}
    </span>

    <div :class="$style.options">
      <BrandButton
        v-if="!isFlux"
        size="sm"
        design="outlined"
        :class="$style.button"
        data-testid="requirementToolbarOpenOptionsButtton"
        @click="openModal(modalName.options)"
      >
        {{ $t('common.options') }}

        <XIcon
          icon="ellipsis-h"
          icon-style="fas"
          right
        />
      </BrandButton>

      <BrandButton
        v-if="!isFlux"
        model="icon"
        :class="[$style.button, $style.mobile]"
        data-testid="requirementToolbarOpenOptionsMobileButtton"
        @click="openModal(modalName.options)"
      >
        <XIcon
          icon="ellipsis-h"
          icon-style="fas"
        />
      </BrandButton>
    </div>
  </div>

  <template v-if="!isFlux">
    <RequirementOptionsModal
      v-if="isModalOpen(modalName.options)"
      :show-refusal-option="!isMassiveSignature && refusableDocuments.length > 0"
      @refuse="openModal(modalName.refusal)"
      @close="closeModal()"
    />

    <RequirementRefusalModal
      v-if="isModalOpen(modalName.refusal)"
      :document-keys="refusableDocuments"
      @close="closeModal()"
    />
  </template>
</template>

<style lang="scss" module>
.toolbar {
  --progress: 0%;

  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-small-xx) var(--space-regular);
  background-color: var(--color-neutral-0);

  &:before,
  &:after {
    --progressbar-height: 2px;
    --progressbar-width: calc(var(--progress) + var(--space-regular));

    position: absolute;
    bottom: calc(var(--progressbar-height) * -1);
    left: calc(var(--space-regular) * -1);
    content: '';
    width: var(--progressbar-width);
    height: var(--progressbar-height);
  }

  &:after {
    background-color: var(--color-neutral-1000);
    transition: width var(--time-transition-base) ease;
  }

  &:before {
    --progress: 100%;

    background-color: var(--color-neutral-500);
  }

  &.hideProgress:before,
  &.hideProgress:after {
    display: none;
  }
}

.options {
  display: flex;
  justify-content: flex-end;
  grid-area: optionsButton;

  .button {
    &:not(.mobile) {
      @media screen and (max-width: 1199px) {
        display: none;
        background: blue;
      }
    }

    &.mobile {
      @media screen and (min-width: 1200px) {
        display: none;
        background: red;
      }
    }
  }
}
</style>
